// 題庫匯出／批次匯入共用：欄位定義、CSV 讀寫、與現有題目比對
// 原則：檔案中「有出現」的欄位才寫入；沒出現的欄位保留資料庫原值（避免清掉圖片、音訊等）
export type Item = Record<string, any>;
export type Incoming = { _id?: string; item: Item };
export type Update = { id: string; item: Item; changed: string[] };

// 題目可匯入的欄位（依顯示順序）
export const FIELDS = ['t', 'q', 'o', 'a', 'e', 'img', 'audio', 'passage', 'year', 'num', 'u', 'tag', 'p', 'noImgNeeded'] as const;
export const FIELD_LABEL: Record<string, string> = {
  t: '題型', q: '題目', o: '選項', a: '答案', e: '解說', img: '圖片', audio: '音訊',
  passage: '閱讀文章', year: '年份', num: '題號', u: '單元', tag: '標籤', p: '配對', noImgNeeded: '不需圖片',
};

// CSV 欄位：標題名稱 → 題目欄位（選項另外處理 option1~option4）
export const CSV_COLUMNS: [string, string][] = [
  ['id', '_id'], ['type', 't'], ['question', 'q'], ['option1', ''], ['option2', ''], ['option3', ''], ['option4', ''],
  ['answer', 'a'], ['explanation', 'e'], ['img', 'img'], ['audio', 'audio'], ['passage', 'passage'],
  ['year', 'year'], ['num', 'num'], ['unit', 'u'],
];

// ── CSV ────────────────────────────────────────────────────────────────
export function escCsv(v: unknown): string {
  const s = v === undefined || v === null ? '' : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// 解析整份 CSV（支援引號內的逗號、換行與 "" 跳脫）
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [], cur = '', q = false;
  const s = text.replace(/^﻿/, '');
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (q) {
      if (ch === '"' && s[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') q = false;
      else cur += ch;
    } else if (ch === '"') q = true;
    else if (ch === ',') { row.push(cur); cur = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && s[i + 1] === '\n') i++;
      row.push(cur); rows.push(row); row = []; cur = '';
    } else cur += ch;
  }
  if (cur !== '' || row.length) { row.push(cur); rows.push(row); }
  return rows.filter(r => r.some(c => c.trim() !== ''));
}

export function itemsToCsv(items: Item[]): string {
  const header = CSV_COLUMNS.map(c => c[0]).join(',');
  const lines = items.map(it => CSV_COLUMNS.map(([name, key]) => {
    if (name.startsWith('option')) return escCsv((it.o ?? [])[Number(name.slice(6)) - 1] ?? '');
    return escCsv(it[key]);
  }).join(','));
  return '﻿' + [header, ...lines].join('\n');
}

const toNum = (v: string) => (v.trim() === '' ? '' : Number(v));

// 依標題列辨識欄位；舊格式（沒有 img 等欄）讀不到的欄位就不寫入
function csvToIncoming(text: string): Incoming[] {
  const rows = parseCsv(text);
  if (!rows.length) return [];
  const header = rows[0].map(h => h.trim().toLowerCase());
  const col = (name: string) => header.indexOf(name);
  return rows.slice(1).map(r => {
    const get = (name: string) => { const i = col(name); return i < 0 ? undefined : (r[i] ?? ''); };
    const item: Item = {};
    for (const [name, key] of CSV_COLUMNS) {
      if (!key || key === '_id') continue;
      const v = get(name);
      if (v === undefined) continue;
      item[key] = key === 'a' || key === 'year' || key === 'num' ? toNum(v) : v.trim();
    }
    const opts = ['option1', 'option2', 'option3', 'option4'].map(get);
    if (opts.some(o => o !== undefined)) {
      const o = opts.map(x => (x ?? '').trim());
      while (o.length && o[o.length - 1] === '') o.pop();   // 三選一：去掉尾端空白選項
      item.o = o;
    }
    if (item.t === undefined || item.t === '') item.t = 'q';
    return { _id: (get('id') ?? '').trim() || undefined, item };
  });
}

// ── JSON ───────────────────────────────────────────────────────────────
function jsonToIncoming(text: string): Incoming[] {
  const parsed = JSON.parse(text);
  if (!Array.isArray(parsed)) throw new Error('JSON 需為題目陣列');
  const alias: Record<string, string> = { question: 'q', options: 'o', answer: 'a', explanation: 'e', unit: 'u' };
  return parsed.map((raw: any) => {
    const item: Item = {};
    for (const [k, v] of Object.entries(raw ?? {})) {
      const key = alias[k] ?? k;
      if ((FIELDS as readonly string[]).includes(key)) item[key] = v;
    }
    if (item.a !== undefined && item.a !== '') item.a = Number(item.a);
    if (!item.t) item.t = 'q';
    return { _id: raw?._id || undefined, item };
  });
}

export function parseBatch(text: string, filename: string): Incoming[] {
  return filename.toLowerCase().endsWith('.json') ? jsonToIncoming(text) : csvToIncoming(text);
}

// ── 驗證與比對 ─────────────────────────────────────────────────────────
export function isValid(it: Item): boolean {
  if (it.t === 'm') return Array.isArray(it.p) && it.p.length > 0;
  return !!it.q && Array.isArray(it.o) && it.o.filter((x: string) => x !== '').length >= 2;
}

const isUrl = (v: unknown) => typeof v === 'string' && /^https?:\/\//.test(v);
export function mediaWarnings(list: Incoming[]): string[] {
  const out: string[] = [];
  list.forEach((r, i) => ['img', 'audio'].forEach(k => {
    const v = r.item[k];
    if (v && !isUrl(v)) out.push(`第 ${i + 1} 筆的${FIELD_LABEL[k]}不是有效網址：${String(v).slice(0, 40)}`);
  }));
  return out;
}

// 空字串、undefined、null 視為相同（皆為「沒有」）
const norm = (v: unknown) => (v === undefined || v === null || v === '' ? '' : JSON.stringify(v));
export function changedFields(cur: Item, inc: Item): string[] {
  return Object.keys(inc).filter(k => norm(cur[k]) !== norm(inc[k]));
}

export function planBatch(incoming: Incoming[], existing: Map<string, Item>) {
  const toUpdate: Update[] = [];
  const toAdd: Item[] = [];
  let skipped = 0;
  for (const { _id, item } of incoming) {
    if (_id && existing.has(_id)) {
      const changed = changedFields(existing.get(_id)!, item);
      if (changed.length) toUpdate.push({ id: _id, item: Object.fromEntries(changed.map(k => [k, item[k]])), changed });
      else skipped++;
    } else {
      toAdd.push(item);
    }
  }
  return { toUpdate, toAdd, skipped };
}
